
import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Trash2, Database, ArrowRight } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import DatabaseSchemaMapper from '@/components/agent-builder/DatabaseSchemaMapper';

interface DatabaseMappingStepProps {
  agentData: any;
  setAgentData: (data: any) => void;
  userId: string;
}

const DatabaseMappingStep: React.FC<DatabaseMappingStepProps> = ({ agentData, setAgentData, userId }) => {
  const { toast } = useToast();
  const [databaseType, setDatabaseType] = useState<string>(agentData.databaseType || 'mysql');
  const [schemaText, setSchemaText] = useState<string>(agentData.databaseSchema || '');
  const [schemaMapping, setSchemaMapping] = useState<any[]>(agentData.schemaMapping || []);
  
  const handleSchemaChange = (schema: string) => {
    setSchemaText(schema);
    setAgentData({
      ...agentData,
      databaseSchema: schema
    });
  };
  
  const handleDatabaseTypeChange = (type: string) => {
    setDatabaseType(type);
    setAgentData({
      ...agentData,
      databaseType: type
    });
  };

  const handleSchemaGenerate = () => {
    toast({
      title: "Schema analyzed",
      description: "Database schema has been analyzed and mapped to artifacts"
    });
    
    // In a real implementation, this would call an API to analyze the schema
    setTimeout(() => {
      const generatedMapping = [
        {
          id: 'mapping-1',
          table: 'customers',
          column: 'full_name',
          artifactId: agentData.artifacts?.[0]?.id,
          functionId: agentData.functions?.[0]?.id,
          extractionPrompt: 'Extract the full name from the customer ID document'
        },
        {
          id: 'mapping-2',
          table: 'customers',
          column: 'id_number',
          artifactId: agentData.artifacts?.[0]?.id,
          functionId: agentData.functions?.[0]?.id,
          extractionPrompt: 'Extract the ID number from the customer ID document'
        }
      ];
      
      setSchemaMapping(generatedMapping);
      setAgentData({
        ...agentData,
        schemaMapping: generatedMapping
      });
      
      toast({
        title: "Mapping complete",
        description: `Generated ${generatedMapping.length} field mappings`
      });
    }, 1500);
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Database Schema Extraction</h3>
        <p className="text-sm text-gray-600">
          Define your database schema to map extracted information from artifacts.
        </p>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="databaseType">Database Type</Label>
              <Select 
                value={databaseType} 
                onValueChange={handleDatabaseTypeChange}
              >
                <SelectTrigger id="databaseType" className="mt-1">
                  <SelectValue placeholder="Select database type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mysql">MySQL</SelectItem>
                  <SelectItem value="postgresql">PostgreSQL</SelectItem>
                  <SelectItem value="mongodb">MongoDB</SelectItem>
                  <SelectItem value="sqlserver">SQL Server</SelectItem>
                  <SelectItem value="oracle">Oracle</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="connectionString">Connection String (Optional)</Label>
              <Input
                id="connectionString"
                placeholder="e.g., mysql://user:password@localhost:3306/dbname"
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                For development only. Production will use environment variables.
              </p>
            </div>
          </div>

          <div>
            <Label htmlFor="schemaDefinition">Database Schema</Label>
            <Textarea
              id="schemaDefinition"
              placeholder="Paste your database schema definition here..."
              value={schemaText}
              onChange={(e) => handleSchemaChange(e.target.value)}
              className="mt-1 min-h-[150px] font-mono text-sm"
            />
          </div>

          <Button
            onClick={handleSchemaGenerate}
            className="bg-[#0EA5E9] text-white hover:bg-[#0EA5E9]/90"
            disabled={!schemaText || agentData.artifacts?.length === 0 || agentData.functions?.length === 0}
          >
            <Database className="h-4 w-4 mr-2" /> Analyze Schema & Generate Mappings
          </Button>
        </div>

        {schemaMapping.length > 0 && (
          <div className="space-y-3 mt-4">
            <h4 className="font-medium">Field Mappings:</h4>
            <div className="grid grid-cols-1 gap-3">
              {schemaMapping.map(mapping => {
                const artifact = agentData.artifacts?.find((a: any) => a.id === mapping.artifactId);
                const func = agentData.functions?.find((f: any) => f.id === mapping.functionId);
                
                return (
                  <Card key={mapping.id} className="bg-gray-50">
                    <CardContent className="p-3">
                      <div className="flex items-center">
                        <div className="flex-1">
                          <div className="flex items-center text-sm">
                            <span className="font-medium">{mapping.table}.{mapping.column}</span>
                            <ArrowRight className="h-3 w-3 mx-2" />
                            <span>{artifact?.name || 'Unknown artifact'}</span>
                          </div>
                          <p className="text-xs text-gray-600 mt-1">
                            Function: {func?.name || 'Unknown function'}
                          </p>
                          <p className="text-xs text-gray-600 mt-1">
                            Extraction: {mapping.extractionPrompt}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        <DatabaseSchemaMapper userId={userId} />
      </div>
    </div>
  );
};

export default DatabaseMappingStep;
